import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Tooltip, Tree } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Input, Search, Icon, Modal, Toggle } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import LocationService from '../../domain/Location/LocationService';
import Map, { Marker, NavigationControl } from 'react-map-gl';
import CreateLocationModal from '../../components/Locations/CreateLocationModal';
import Pin from '@/components/Locations/CreateLocationModal/pin';
import './locationDetail.scss';

const locationService = new LocationService();
const mapboxApiAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const initialViewState = {
	longitude: -86.250275,
	latitude: 41.676388,
	zoom: 11,
	pitch: 0,
	bearing: 0,
};

const statusOptions: ComboboxOption[] = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' },
];

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

const LocationsDetailView: React.FC = () => {
	const organizationSettings = localStorage.getItem('organizationSettings')!;
	const mapStyle =
		JSON.parse(organizationSettings)?.mapSettings?.settings?.style ||
		'mapbox://styles/mapbox/light-v11';
	const { locationId } = useParams();
	const query = useQuery();
	const navigate = useNavigate();

	const [treeData, setTreeData] = useState<any[]>([]);
	const [locationSelect, setLocationSelect] = useState<any | null>(null);
	const [isEdit, setIsEdit] = useState(false);
	const [showCreationModal, setShowCreationModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showTree, setShowTree] = useState(false);
	const [viewport, setViewport] = useState(initialViewState);
	const [marker, setMarker] = useState<any>(null);
	const [queryFilter, setQueryFilter] = useState('');
	const [tempQueryFilter, setTempQueryFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [existData, setExistData] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(true);

	const [isOpen, setIsOpen] = useState(false);

	function treeNodeFromLocation(location: any): any {
		return {
			key: location.id,
			id: location.id,
			label: location.name,
			value: JSON.stringify(location),
			latitude: location.latitude,
			longitude: location.longitude,
			visible: location.visible,
			...(location.childrens?.length
				? { children: location.childrens.map(treeNodeFromLocation) }
				: {}),
		};
	}

	function locationsFromTreeNode(treeNode: any, level: number): any {
		if (!treeNode.value) return;
		const location = JSON.parse(treeNode.value.toString());
		return {
			...(location.id && { id: location.id }),
			name: location.name,
			latitude: location.latitude,
			longitude: location.longitude,
			visible: location.visible,
			zoom: location.zoom,
			level,
			networkType: location.networkType,
			...(treeNode.children?.length
				? {
						childLocations: treeNode.children.map((child: any) =>
							locationsFromTreeNode(child, level + 1),
						),
					}
				: {}),
		};
	}

	function searchAndAdd(array: any[], key: string, value: any, newValue: any): any[] {
		return array.map((item) => {
			if (item[key] === value) {
				const children = item.children ?? [];
				return { ...item, children: [...children, newValue] };
			} else if (item.children) {
				return {
					...item,
					children: searchAndAdd(item.children, key, value, newValue),
				};
			}
			return item;
		});
	}

	function searchAndReplace(array: any[], id: any, newValue: any): any[] {
		return array.map((item) => {
			if (item.id === id) {
				return treeNodeFromLocation(newValue);
			} else if (item.children) {
				return {
					...item,
					children: searchAndReplace(item.children, id, newValue),
				};
			}
			return item;
		});
	}

	function countChildren(node: any): number {
		if (!node.children) return 0;
		return node.children.reduce((acc: number, child: any) => acc + 1 + countChildren(child), 0);
	}

	const getLocations = async () => {
		setShowTree(false);
		if (!locationId) return;

		const security = localStorage.getItem('security');
		if (!security) return;

		const { organizationId } = JSON.parse(security);

		try {
			const res = await locationService.getLocationsByCountryOrganization(
				locationId,
				organizationId,
				queryFilter ?? undefined,
				statusFilter ?? undefined,
			);

			const data = res.map(treeNodeFromLocation);
			setTreeData(data);

			if (isFirstLoad) {
				setExistData(data.length > 0);
				setIsFirstLoad(false);
			}

			if (data?.length) {
				const row = JSON.parse(data[0].value);
				if (row.latitude && row.longitude) {
					setMarker({ latitude: row.latitude, longitude: row.longitude });
					setViewport({
						...viewport,
						latitude: parseFloat(row.latitude),
						longitude: parseFloat(row.longitude),
						zoom: parseFloat(row.zoom),
					});
				}
			}

			setShowTree(true);
		} catch (err) {
			console.error('Error loading locations', err);
			if (isFirstLoad) {
				setExistData(false);
				setIsFirstLoad(false);
			}
		}
	};

	const handleUpdateLocations = async (newTreeData: any) => {
		if (!locationId) return;

		const security = localStorage.getItem('security');
		if (!security) return;

		const { organizationId } = JSON.parse(security);

		const newLocations = newTreeData.map((node: any) => locationsFromTreeNode(node, 1));

		try {
			const res = await locationService.postLocationsByCountryOrganization(
				locationId,
				organizationId,
				newLocations,
			);
			if (res) {
				setShowCreationModal(false);
				getLocations();
			}
		} catch (err) {
			console.error('Error updating locations', err);
		}
	};

	const onDrop = () => {
		setTreeData([...treeData]);
	};

	const renderTreeNode = (item: any) => {
		const row = JSON.parse(item?.value?.toString() ?? '{}');

		return (
			<div
				className="flex w-full justify-between"
				role="button"
				onClick={() => {
					if (row.latitude && row.longitude) {
						setMarker({ latitude: row.latitude, longitude: row.longitude });
						setViewport({
							...viewport,
							latitude: parseFloat(row.latitude),
							longitude: parseFloat(row.longitude),
							zoom: parseFloat(row.zoom),
						});
					}
				}}
			>
				<span>{item.label}</span>
				<div className="flex">
					<Toggle
						checkedText="ON"
						uncheckedText="OFF"
						size="small"
						defaultChecked={row.visible}
						onChange={() => handleChangeVisibleLocation(item)}
						className="mr-8"
					/>
					<span
						className="mr-8 link"
						onClick={() => {
							setIsEdit(false);
							setLocationSelect(item);
							setShowCreationModal(true);
						}}
					>
						Add location
					</span>
					<Tooltip title="Delete">
						<DeleteOutlined
							className="mr-1 icon"
							onClick={() => {
								setLocationSelect(item);
								setShowDeleteModal(true);
							}}
						/>
					</Tooltip>
					<Tooltip title="Edit">
						<EditOutlined
							className="mr-1 icon"
							onClick={() => {
								setIsEdit(true);
								setLocationSelect(item);
								setShowCreationModal(true);
							}}
						/>
					</Tooltip>
				</div>
			</div>
		);
	};

	const handleChangeVisibleLocation = async (location: any) => {
		if (!location?.value) return;

		const security = localStorage.getItem('security');
		if (!security) return;

		const { organizationId } = JSON.parse(security);
		const locationData = JSON.parse(location.value.toString());

		try {
			const res = await locationService.changeEnableLocation(
				organizationId,
				location.id,
				locationData.visible,
			);
			if (res) {
				getLocations();
			}
		} catch (err) {
			console.error('Error changing visibility', err);
		}
	};

	useEffect(() => {
		if (!locationId) {
			navigate('/locations');
		} else {
			getLocations();
		}
	}, [statusFilter, queryFilter]);

	return (
		<div style={{ padding: '0 3rem' }}>
			<div>
				<Button
					size="small"
					label="Back to regions"
					iconName="arrow-left"
					iconColor="#434B56"
					style={{ backgroundColor: '#F7F9FA', color: '#434B56', border: 0 }}
					onClick={() => navigate('/locations')}
				/>
			</div>

			{existData ? (
				<div
					className="grid"
					style={{
						gridTemplateColumns: '1fr minmax(300px, 0.7fr)',
						columnGap: '2rem',
					}}
				>
					<div className="pt-3">
						<span className="text-title-page">{query.get('name')} locations</span>

						<div className="flex items-center mt-4">
							<div className="mr-2 ml-2">
								<Search
									placeholder="Search"
									size="small"
									onChange={(e) => {
										if (e.target.value == '') {
											setQueryFilter('');
											setTempQueryFilter('');
										}
									}}
									onBlur={(e) => {
										setQueryFilter(e.target.value);
										setTempQueryFilter(e.target.value);
									}}
									onKeyDown={(e) => {
										if (e.target) {
											const element = e.currentTarget as HTMLInputElement;
											if (e.key === 'Enter') setQueryFilter(element.value);
										}
									}}
									onClear={() => {
										setQueryFilter('');
										setTempQueryFilter('');
									}}
									showClearButton
								/>
							</div>
							<div style={{ width: 120 }}>
								<Combobox
									items={statusOptions}
									onValueChange={(val) => setStatusFilter(val)}
									onClear={() => setStatusFilter('')}
									searchable={false}
									size="small"
								>
									<ComboboxInput placeholder="Select" />
									<ComboboxContent>
										<ComboboxEmpty>No options.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
							<div className="ml-2.5">
								<Button
									variant="primary"
									size="small"
									label="Add location"
									iconName="plus-circle"
									iconColor="white"
									onClick={() => {
										setIsEdit(false);
										setLocationSelect(null);
										setShowCreationModal(true);
									}}
								/>
							</div>
						</div>
						<div className="mt-3">
							<Card title="Locations" style={{ overflow: 'auto' }}>
								{showTree && (
									<Tree
										className="location-list"
										treeData={treeData}
										draggable
										defaultExpandAll
										onDrop={onDrop}
										titleRender={renderTreeNode}
										style={{ height: 'calc(100vh - 380px)', overflow: 'auto' }}
									/>
								)}
							</Card>
						</div>
					</div>

					<div className="map-container flex items-center">
						<div style={{ width: '100%', height: '80%' }}>
							<Map
								{...viewport}
								mapboxAccessToken={mapboxApiAccessToken}
								mapStyle={mapStyle}
								style={{ width: '100%', height: '100%' }}
								onMove={(evt) => setViewport(evt.viewState)}
							>
								{marker && (
									<Marker
										latitude={marker.latitude}
										longitude={marker.longitude}
										offset={[-20, -10]}
									>
										<Pin size={30} />
									</Marker>
								)}
								<NavigationControl />
							</Map>
						</div>
					</div>
				</div>
			) : (
				<div className="pt-3 flex column" style={{ height: 'calc(100vh - 150px)' }}>
					<span className="text-title-page">{query.get('name')} locations</span>
					<div className="flex column self-center mb-auto mt-auto items-center">
						<span className="mb-2 text-center" style={{ color: '#232B36', width: '180px' }}>
							Now you only need to click on “Add location” button to continue!
						</span>
						<Button
							className="flex items-center self-center"
							style={{
								backgroundColor: '#023E65',
								color: 'white',
								width: '130px',
								height: '40px',
							}}
							label="Add location"
							onClick={() => {
								setIsEdit(false);
								setLocationSelect(null);
								setShowCreationModal(true);
							}}
						/>
					</div>
				</div>
			)}

			<CreateLocationModal
				isEdit={isEdit}
				isModalOpen={showCreationModal}
				selectLocation={locationSelect}
				onAcept={(location, selected) => {
					const id = selected?.id;
					const newNode = {
						key: `${Date.now()}-${Math.random()}`,
						label: location.name,
						value: JSON.stringify(location),
						visible: location.visible,
					};

					if (isEdit && id) {
						const updated = searchAndReplace(treeData, id, location);
						handleUpdateLocations(updated);
					} else {
						const updated = selected
							? searchAndAdd([...treeData], 'value', selected.value, newNode)
							: [...treeData, newNode];
						handleUpdateLocations(updated);
					}
				}}
				onCancel={() => setShowCreationModal(false)}
			/>

			<Modal
				isOpen={showDeleteModal}
				size="s"
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={true}
				testId="my-modal"
				onClose={() => setShowDeleteModal(false)}
			>
				<Modal.Header>
					<div className="flex column items-center">
						<span
							className="mb-2"
							style={{ color: '#232B36', fontSize: '1.2rem', fontWeight: 'bold' }}
						>
							Delete Location?
						</span>
					</div>
				</Modal.Header>

				<Modal.Content>
					<div className="flex column items-center">
						<span
							className="mb-3"
							style={{ color: '#74788D', fontSize: '1rem', fontWeight: '600' }}
						>
							Are you sure you want to delete {locationSelect?.label}
						</span>

						{locationSelect && countChildren(locationSelect) > 0 && (
							<div
								className="flex column mb-2"
								style={{
									background: 'rgba(255, 211, 211, 0.5)',
									borderRadius: '1rem',
									padding: '1rem',
									color: '#F46A6A',
								}}
							>
								<b>Warning</b>
								<span>
									By deleting this location, {countChildren(locationSelect)} other locations will
									also be deleted
								</span>
							</div>
						)}
					</div>
				</Modal.Content>
				<Modal.Footer>
					<div className="flex column items-center">
						<div className="flex">
							<div className="mr-3">
								<Button
									label="No"
									showIcon={false}
									style={{
										width: '85px',
										height: '40px',
										backgroundColor: '#F7F9FA',
										color: '#434B56',
										border: '1px solid #C4CCD5',
									}}
									onClick={() => setShowDeleteModal(false)}
								/>
							</div>

							<Button
								label="Yes"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#00203C',
									color: 'white',
								}}
								onClick={async () => {
									try {
										const id = JSON.parse(locationSelect?.value)?.id;
										if (id) {
											await locationService.deleteLocation(id);
											setShowDeleteModal(false);
											getLocations();
										}
									} catch (err) {
										console.error('Error deleting location', err);
									}
								}}
							/>
						</div>
					</div>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default LocationsDetailView;
